import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
    FaArrowLeft, 
    FaUser, 
    FaStore, 
    FaMoneyBillWave, 
    FaCalendarAlt, 
    FaCheckCircle, 
    FaClock, 
    FaTimesCircle,
    FaShoppingCart,
    FaBox,
    FaPhone,
    FaMapMarkerAlt,
    FaTruck,
    FaEnvelope,
    FaCreditCard
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const BranchOrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch (error) {
            }
        }
        return null;
    };

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                navigate('/login');
                return;
            }

            // تحديد الـ endpoint بناءً على الـ role
            let endpoint = '';
            if (userRole === 'store') {
                endpoint = `${BASE_URL}/store_order_details/${id}`;
            } else if (userRole === 'pharmacy') {
                endpoint = `${BASE_URL}/main-branch/orders/${id}`;
            } else {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة');
                navigate('/');
                return;
            }

            const response = await axios.get(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data) {
                let orderData = {};
                let orderItemsData = [];

                // للـ store role
                if (response.data.result) {
                    const result = response.data.result;
                    orderData = {
                        id: result.order.id,
                        status: result.order.status,
                        placed_at: result.order.placed_at,
                        store_id: result.order.store_id,
                        branch_id: result.order.branch_id,
                        subtotal: result.breakdown.meals_total,
                        discount_total: result.breakdown.discount_total,
                        delivery_fees: result.breakdown.delivery_fees,
                        extra_fees: result.breakdown.additions,
                        total: result.breakdown.total,
                        payment_method: result.payment.selected,
                        payment_status: 'pending',
                        user_name: result.order.client_name || 'غير محدد',
                        prof_img: '',
                        items_count: result.items.length,
                        breakdown: result.breakdown,
                        payment: result.payment,
                        nearest_branch: result.order.nearest_branch || null
                    };
                    orderItemsData = result.items;
                }
                // للـ pharmacy role
                else if (response.data.data) {
                    const data = response.data.data;
                    orderData = {
                        id: data.id,
                        status: data.status,
                        placed_at: data.created_at,
                        store_id: 0,
                        branch_id: 0,
                        subtotal: data.subtotal,
                        discount_total: data.discount_total,
                        delivery_fees: data.delivery_fees,
                        extra_fees: data.extra_fees,
                        total: data.total,
                        payment_method: data.payment_method,
                        payment_status: data.payment_status,
                        user_name: data.client.name,
                        prof_img: '',
                        items_count: data.items ? data.items.length : 0,
                        branch_name: data.branch || 'غير محدد',
                        breakdown: {
                            meals_total: data.subtotal,
                            additions: data.extra_fees,
                            delivery_fees: data.delivery_fees,
                            discount_total: data.discount_total,
                            total: data.total
                        },
                        payment: {
                            selected: data.payment_method,
                            options: [data.payment_method]
                        },
                        address: data.address,
                        notes: data.notes,
                        delivery_method: data.delivery_method,
                        delivery_status: data.delivery_status,
                        delivery: data.delivery,
                        client: data.client,
                        nearest_branch: data.nearest_branch || null
                    };
                    // تحويل items للـ pharmacy
                    orderItemsData = data.items ? data.items.map(item => ({
                        id: item.id,
                        product_id: item.product_id,
                        product_name: item.meta?.product_name || item.title || 'منتج غير محدد',
                        unit_name: item.meta?.unit_name || 'غير محدد',
                        color_name: item.meta?.color_name,
                        color_code: item.meta?.color_code,
                        quantity: item.quantity,
                        price: item.base_price,
                        final_price: item.final_price,
                        line_total: item.line_total,
                        product_image: item.meta?.product_image,
                        images: item.meta?.product_image ? [item.meta.product_image] : []
                    })) : [];
                } else {
                    throw new Error('فشل في جلب تفاصيل الأوردر');
                }
                
   
                setOrder(orderData);
                setOrderItems(orderItemsData);
            } else {
                throw new Error('فشل في جلب تفاصيل الأوردر');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الأوردر', { containerId: 'main-toast-container' });
            
            // تحديد الـ route للعودة بناءً على الـ role
            const userRole = getUserRole();
            if (userRole === 'store') {
                navigate('/store/orders');
            } else if (userRole === 'pharmacy') {
            navigate('/main-branch/orders');
            } else {
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchOrderDetails();
        }
    }, [id]);

    // دالة تغيير حالة الطلب
    const handleChangeStatus = async (newStatus) => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                return;
            }

            // التحقق من أن المستخدم pharmacy
            if (userRole !== 'pharmacy') {
                toast.error('غير مصرح لك بتغيير حالة الطلب', { containerId: 'main-toast-container' });
                return;
            }

            const endpoint = `${BASE_URL}/main-branch/orders/${id}/change-status`;
            
            const response = await axios.post(endpoint, 
                { status: newStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            if (response.data) {
                toast.success(`تم تغيير حالة الطلب إلى ${newStatus === 'preparing' ? 'قيد التحضير' : 'ملغي'} بنجاح`, { containerId: 'main-toast-container' });
                // إعادة تحميل تفاصيل الطلب
                fetchOrderDetails();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في تغيير حالة الطلب', { containerId: 'main-toast-container' });
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'placed':
                return <span className="badge bg-info">تم الطلب</span>;
            case 'confirmed':
                return <span className="badge bg-info">مؤكد</span>;
            case 'preparing':
                return <span className="badge bg-warning">قيد التحضير</span>;
            case 'ready':
                return <span className="badge bg-primary">جاهز</span>;
            case 'completed':
                return <span className="badge bg-success">مكتمل</span>;
            case 'delivered':
                return <span className="badge bg-success">تم التوصيل</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getPaymentStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <span className="badge bg-success">مدفوع</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'failed':
                return <span className="badge bg-danger">فشل</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getDeliveryStatusBadge = (status) => {
        switch (status) {
            case 'delivered':
                return <span className="badge bg-success">تم التسليم</span>;
            case 'accepted':
                return <span className="badge bg-info">مقبول</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">جاري التحميل...</span>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <FaTimesCircle className="text-danger mb-3" style={{ fontSize: '3rem' }} />
                    <h5 className="text-muted">الأوردر غير موجود</h5>
                    <button 
                        className="btn btn-primary mt-3"
                        onClick={() => {
                            const userRole = getUserRole();
                            if (userRole === 'store') {
                                navigate('/store/orders');
                            } else if (userRole === 'pharmacy') {
                                navigate('/main-branch/orders');
                            } else {
                                navigate('/');
                            }
                        }}
                    >
                        العودة للأوردرات
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    {/* Header */}
                    <div className="d-flex align-items-center mb-4">
                        <button 
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => {
                                const userRole = getUserRole();
                                if (userRole === 'store') {
                                    navigate('/store/orders');
                                } else if (userRole === 'pharmacy') {
                                    navigate('/main-branch/orders');
                                } else {
                                    navigate('/');
                                }
                            }}
                        >
                            <FaArrowLeft className="mx-1" />
                            العودة
                        </button>
                        <div>
                            <h4 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                <FaShoppingCart className="mx-2" />
                                تفاصيل الأوردر #{order.id}
                            </h4>
                            <p className="text-muted mb-0">معلومات مفصلة عن الأوردر</p>
                        </div>
                    </div>
                    {/* Nearest Branch Info */}
                    {order.nearest_branch && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                    <FaStore className="mx-2" />
                                    أقرب فرع للعميل
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                <FaStore className="text-white" />
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-semibold">اسم الفرع</h6>
                                                <p className="text-muted mb-0">{order.nearest_branch.name}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                <FaMapMarkerAlt className="text-white" />
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-semibold">المسافة للفرع</h6>
                                                <p className="text-muted mb-0">{order.nearest_branch.distance_km} كم</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        {/* Order Info Card */}
                        <div className="col-lg-8">
                            <motion.div 
                                className="card border-0 shadow-sm mb-4"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="card-header bg-white border-0 py-3">
                                    <h5 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                        <FaBox className="mx-2" />
                                        معلومات الأوردر
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                {order.prof_img && order.prof_img !== "" ? (
                                                    <img 
                                                        src={order.prof_img} 
                                                        alt="صورة العميل"
                                                        className="mx-3"
                                                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                                                    />
                                                ) : (
                                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaUser className="text-white" style={{ fontSize: '20px' }} />
                                                </div>
                                                )}
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">العميل</h6>
                                                    <p className="text-muted mb-0">{order.user_name || 'غير محدد'}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaStore className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">عدد المنتجات</h6>
                                                    <p className="text-muted mb-0">{order.items ? order.items.length : order.items_count || 0} منتج</p>
                                                </div>
                                            </div>
                                        </div>
                                        {order.branch_name && order.branch_name !== 'غير محدد' && (
                                            <div className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaStore className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">الفرع</h6>
                                                        <p className="text-muted mb-0">{order.branch_name}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {order.address && (
                                            <div className="col-12 mb-3">
                                                <div className="d-flex align-items-start">
                                                    <div className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaMapMarkerAlt className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">العنوان</h6>
                                                        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{order.address}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {order.notes && (
                                            <div className="col-12 mb-3">
                                                <div className="d-flex align-items-start">
                                                    <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaPhone className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">ملاحظات</h6>
                                                        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{order.notes}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-success rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">المجموع الفرعي</h6>
                                                    <p className="text-muted mb-0 fw-bold" style={{ color: '#007bff' }}>{order.breakdown?.meals_total || order.subtotal} ج.م</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaCalendarAlt className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">تاريخ الطلب</h6>
                                                    <p className="text-muted mb-0">{formatDate(order.placed_at)}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">الخصم</h6>
                                                    <p className="text-muted mb-0 fw-bold" style={{ color: (order.breakdown?.discount_total || order.discount_total) > 0 ? '#28a745' : '#6c757d' }}>
                                                        {(order.breakdown?.discount_total || order.discount_total) > 0 ? `-${order.breakdown?.discount_total || order.discount_total}` : '0'} ج.م
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">رسوم التوصيل</h6>
                                                    <p className="text-muted mb-0 fw-bold" style={{ color: (order.breakdown?.delivery_fees || order.delivery_fees) > 0 ? '#ffc107' : '#6c757d' }}>
                                                        {order.breakdown?.delivery_fees || order.delivery_fees} ج.م
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">رسوم إضافية</h6>
                                                    <p className="text-muted mb-0 fw-bold" style={{ color: order.extra_fees > 0 ? '#dc3545' : '#6c757d' }}>
                                                        {order.extra_fees} ج.م
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-success rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">المبلغ الإجمالي</h6>
                                                    <p className="text-muted mb-0 fw-bold" style={{ color: '#28a745' }}>{order.total} ج.م</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">طريقة الدفع</h6>
                                                    <p className="text-muted mb-0">
                                                        {order.payment_method === 'cod' ? 'الدفع عند الاستلام' :
                                                            order.payment_method === 'online' ? 'دفع إلكتروني' :
                                                                order.payment_method === 'wallet' ? 'محفظة إلكترونية' : order.payment_method}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                    <FaMoneyBillWave className="text-white" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-semibold">حالة الدفع</h6>
                                                    <p className="text-muted mb-0">
                                                        {order.payment_status === 'pending' ? 'في الانتظار' : 
                                                         order.payment_status === 'paid' ? 'مدفوع' : 
                                                         order.payment_status === 'failed' ? 'فشل' : order.payment_status}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        {order.delivery_status && (
                                            <div className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaBox className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">حالة التوصيل</h6>
                                                        <p className="text-muted mb-0">
                                                            {order.delivery_status === 'accepted' ? 'مقبول' : 
                                                             order.delivery_status === 'rejected' ? 'مرفوض' : 
                                                             order.delivery_status === 'pending' ? 'في الانتظار' : order.delivery_status}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {order.delivery_method && (
                                            <div className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaStore className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">طريقة التوصيل</h6>
                                                        <p className="text-muted mb-0">
                                                            {order.delivery_method === 'delivery' ? 'توصيل' : 
                                                             order.delivery_method === 'pickup' ? 'استلام' : order.delivery_method}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {order.payment && order.payment.options && (
                                            <div className="col-12 mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                        <FaMoneyBillWave className="text-white" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">خيارات الدفع المتاحة</h6>
                                                        <div className="d-flex gap-2 mt-1">
                                                            {order.payment.options.map((option, index) => (
                                                                <span key={index} className={`badge ${option === order.payment_method ? 'bg-primary' : 'bg-light text-dark'}`}>
                                                                    {option === 'cod' ? 'الدفع عند الاستلام' :
                                                                        option === 'online' ? 'دفع إلكتروني' :
                                                                            option === 'wallet' ? 'محفظة إلكترونية' : option}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Delivery Info */}
                            {order.delivery && (order.delivery.name || order.delivery.phone || order.delivery.email || order.delivery.vehicle_number) && (
                                <motion.div 
                                    className="card border-0 shadow-sm mb-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.1 }}
                                >
                                    <div className="card-header bg-white border-0 py-3">
                                        <h5 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                            <FaTruck className="mx-2" />
                                            معلومات المندوب
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {order.delivery.name && (
                                                <div className="col-md-6 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                            <FaUser className="text-white" />
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-semibold">الاسم</h6>
                                                            <p className="text-muted mb-0">{order.delivery.name}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {order.delivery.phone && (
                                                <div className="col-md-6 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-success rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                            <FaPhone className="text-white" />
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-semibold">الهاتف</h6>
                                                            <p className="text-muted mb-0">{order.delivery.phone}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {order.delivery.email && (
                                                <div className="col-md-6 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                            <FaEnvelope className="text-white" />
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-semibold">البريد الإلكتروني</h6>
                                                            <p className="text-muted mb-0">{order.delivery.email}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {order.delivery.vehicle_number && (
                                                <div className="col-md-6 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center mx-3" style={{ width: '50px', height: '50px' }}>
                                                            <FaTruck className="text-white" />
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-semibold">رقم المركبة</h6>
                                                            <p className="text-muted mb-0">{order.delivery.vehicle_number}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Order Items */}
                            {orderItems.length > 0 && (
                                <motion.div 
                                    className="card border-0 shadow-sm"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.1 }}
                                >
                                    <div className="card-header bg-white border-0 py-3">
                                        <h5 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                            <FaShoppingCart className="mx-2" />
                                            عناصر الأوردر
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th className="border-0 fw-semibold text-center">#</th>
                                                        <th className="border-0 fw-semibold text-end">اسم المنتج</th>
                                                        <th className="border-0 fw-semibold text-end">صوره المنتج</th>
                                                        <th className="border-0 fw-semibold text-center">الكمية</th>
                                                        <th className="border-0 fw-semibold text-center">السعر</th>
                                                        <th className="border-0 fw-semibold text-center">المجموع</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orderItems.map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="text-center">
                                                                <span className="fw-semibold text-muted">#{index + 1}</span>
                                                            </td>
                                                            <td className="text-end">
                                                                <div>
                                                                    <h6 className="mb-0 fw-semibold">{item.title || item.product_name || 'منتج غير محدد'}</h6>
                                                                    <small className="text-muted">
                                                                        الوحدة: {item.unit_name && item.unit_name !== 'example' ? item.unit_name : 'غير محدد'}
                                                                    </small>
                                                                    {item.color_name && (
                                                                        <small className="text-muted d-block">اللون: {item.color_name}</small>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <div style={{ position: 'relative', width: '60px', height: '60px', margin: '0 auto' }}>
                                                                    {(() => {
                                                                        const hasImage = item.images && item.images.length > 0 && item.images[0] && item.images[0].trim() !== '';
                                                                        
                                                                        if (hasImage) {
                                                                            return (
                                                                                <img
                                                                                    src={item.images[0]}
                                                                                    alt="صورة المنتج"
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: '8px',
                                                                                        border: '2px solid #e9ecef'
                                                                                    }}
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        e.target.nextSibling.style.display = 'flex';
                                                                                    }}
                                                                                />
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div
                                                                                    className="bg-light d-flex align-items-center justify-content-center border rounded text-muted"
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        borderRadius: '8px',
                                                                                        fontSize: '10px',
                                                                                        textAlign: 'center',
                                                                                        border: '2px solid #e9ecef'
                                                                                    }}
                                                                                >
                                                                                    لا توجد صورة
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })()}
                                                                    {/* Fallback div - يظهر فقط عند فشل تحميل الصورة */}
                                                                    <div
                                                                        className="bg-light d-flex align-items-center justify-content-center border rounded text-muted"
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            borderRadius: '8px',
                                                                            fontSize: '10px',
                                                                            textAlign: 'center',
                                                                            border: '2px solid #e9ecef',
                                                                            display: 'none',
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                            left: 0
                                                                        }}
                                                                    >
                                                                        خطأ في الصورة
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge bg-primary text-white" style={{ fontSize: '14px', padding: '6px 12px' }}>
                                                                    {item.quantity || 1}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="fw-bold" style={{ color: '#28a745' }}>
                                                                    {item.price || 0} ج.م
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="fw-bold" style={{ color: '#184194' }}>
                                                                    {item.line_total || 0} ج.م
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Status Card */}
                        <div className="col-lg-4">
                            <motion.div 
                                className="card border-0 shadow-sm mb-4"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: 0.2 }}
                            >
                                <div className="card-header bg-white border-0 py-3">
                                    <h5 className="mb-0 fw-bold d-flex align-items-center" style={{ color: '#184194' }}>
                                        <FaCheckCircle className="mx-2" />
                                        حالة الطلب
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <h6 className="text-muted">الحالة العامة</h6>
                                        <div className="d-flex align-items-center">
                                            {getStatusBadge(order.status)}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <h6 className="text-muted">حالة الدفع</h6>
                                        <div className="d-flex align-items-center">
                                            <FaCreditCard className="mx-2" style={{ fontSize: '12px' }} />
                                            {getPaymentStatusBadge(order.payment_status)}
                                        </div>
                                    </div>
                                    {order.delivery_status && (
                                        <div className="mb-3">
                                            <h6 className="text-muted">حالة التسليم</h6>
                                            <div className="d-flex align-items-center">
                                                <FaTruck className="mx-2" style={{ fontSize: '12px' }} />
                                                {getDeliveryStatusBadge(order.delivery_status)}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* أزرار تغيير الحالة */}
                                    {getUserRole() === 'pharmacy' && order.status !== 'cancelled' && order.status !== 'preparing' && (
                                        <div className="mt-4 pt-3 border-top">
                                            <h6 className="text-muted mb-3">تغيير الحالة</h6>
                                            <div className="d-grid gap-2">
                                                <button 
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => handleChangeStatus('preparing')}
                                                    disabled={order.status === 'preparing'}
                                                >
                                                    <FaClock className="mx-1" />
                                                    تحضير الطلب
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => {
                                                        if (window.confirm('هل أنت متأكد من إلغاء هذا الطلب؟')) {
                                                            handleChangeStatus('cancelled');
                                                        }
                                                    }}
                                                >
                                                    <FaTimesCircle className="mx-1" />
                                                    إلغاء الطلب
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>

                            <motion.div 
                                className="card border-0 shadow-sm"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: 0.3 }}
                            >
                                <div className="card-body">
                                    <div className="d-grid gap-2">
                                        <button 
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                const userRole = getUserRole();
                                                if (userRole === 'store') {
                                                    navigate('/store/orders');
                                                } else if (userRole === 'pharmacy') {
                                                    navigate('/main-branch/orders');
                                                } else {
                                                    navigate('/');
                                                }
                                            }}
                                        >
                                            <FaArrowLeft className="mx-1" />
                                            العودة للأوردرات
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BranchOrderDetails;
